import { useEffect, useState } from 'react';
import {
  SelfieCameraTypes,
  CallbackCamera,
  ErrorPictureResponse,
  SuccessPictureResponse,
  SupportPictureResponse,
  SelfieCameraType,
  UnicoConfig,
} from 'unico-webframe';
import { useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';

import { imageRecognition } from '~libs';
import { CameraPlaceholder, PhotoConfirmation, Toast } from '~components';
import { Representative, Spouse } from '~services';
import { Response, ParamsFile, ParamsAnalysis } from '~types/getUserData';
import { useLayout } from '~hooks';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { SelfieAnalysis } from '~services/Representative';

import * as amplitude from '@amplitude/analytics-browser';
import { unicoConfig } from '~libs/imageRecognition';

function SelfieCamera({
  onClickCloseCamera,
}: {
  onClickCloseCamera: () => void;
}) {
  const transshipment = useTransshipment();
  const { setLayout } = useLayout();
  const navigate = useNavigate();

  const requestAnalysis =
    transshipment.type === 'spouse'
      ? Spouse.selfieAnalysis
      : Representative.selfieAnalysis;

  const request =
    transshipment.type === 'spouse'
      ? Spouse.addDocument
      : Representative.addDocument;

  const [showPhotoConfirmation, setShowPhotoConfirmation] = useState(false);
  const [base64Image, setBase64Image] = useState('');
  const [encrypted, setEncrypted] = useState('');
  const [isToastVisibleForm, setIsToastVisibleForm] = useState(false);

  const {
    runAsync: selfieAnalysis,
    error: errorAnalysis,
    loading: isLoadingSelfieAnalysis,
  } = useRequest<Response, ParamsAnalysis>(requestAnalysis, {
    manual: true,
    onSuccess: successAnalysis,
  });

  const {
    runAsync: addDocument,
    error,
    loading: isLoadingAddDocument,
  } = useRequest<Response, ParamsFile>(request, {
    manual: true,
    onSuccess: successAddDocument,
  });

  async function successAnalysis(response: SelfieAnalysis) {
    await addDocument(transshipment?.id, {
      file: base64Image,
      type: 'selfie',
      analysis_id: response.analysisId,
    });

    setLayout({ disabledLayout: false, header: {} });
  }

  async function successAddDocument() {
    if (transshipment.type === 'representative') {
      amplitude.track({
        event_type: 'documentation_selfie_sent',
        event_properties: {
          CPF_representatives: transshipment.data.cpf,
        },
      });
    }

    transshipment.nextStep();
  }

  async function handleSuccess() {
    await selfieAnalysis(transshipment?.id, {
      selfie: encrypted,
    });
  }

  const callbacks: CallbackCamera = {
    on: {
      success: function (response: SuccessPictureResponse) {
        setBase64Image(prependImageMimeType(response.base64));
        setShowPhotoConfirmation(true);
        setEncrypted(response.encrypted);
      },
      error: function (error: ErrorPictureResponse) {},
      support: function (error: SupportPictureResponse) {},
    },
  };

  const openSelfieCamera = async (
    jsonPath: UnicoConfig,
    cameraType: SelfieCameraType
  ) => {
    try {
      const response = await imageRecognition.prepareSelfieCamera(
        jsonPath,
        cameraType
      );

      if (response) response.open(callbacks);
    } catch (error) {
      navigate('/web/internet');
    }
  };

  const takeAnotherPhoto = () => {
    setShowPhotoConfirmation(false);
    openSelfieCamera(unicoConfig, SelfieCameraTypes.SMART);
  };

  useEffect(() => {
    if (errorAnalysis) setIsToastVisibleForm(true);
    if (error) throw error;
  }, [error, errorAnalysis]);

  useEffect(() => {
    setLayout({ disabledLayout: true, header: {} });
    openSelfieCamera(unicoConfig, SelfieCameraTypes.SMART);
  }, []);

  return showPhotoConfirmation ? (
    <>
      <PhotoConfirmation
        base64Image={base64Image}
        onClickIsGood={handleSuccess}
        onClickTakeAnother={takeAnotherPhoto}
        isLoadingConfirmation={isLoadingSelfieAnalysis || isLoadingAddDocument}
      />
      <Toast
        variant="error"
        title="Não foi possível enviar a sua selfie, refaça e envie novamente."
        isVisible={isToastVisibleForm}
        lifetime={5000}
        onDismiss={() => setIsToastVisibleForm(false)}
      />
    </>
  ) : (
    <CameraPlaceholder onClickClose={onClickCloseCamera} />
  );
}

function prependImageMimeType(base64Image: string) {
  if (base64Image.includes('data:image/png;base64,')) return base64Image;

  return `data:image/png;base64,${base64Image}`;
}

export default SelfieCamera;
